import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, TextField, Button, Typography, Container, IconButton, InputAdornment, Paper, Avatar, Alert } from '@mui/material';
import { Visibility, VisibilityOff, Person, LockOutlined } from '@mui/icons-material';
import { login } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import logo from '../store_logo/zebra.jpg';

function Login() {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const { clearCart } = useCart();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Clear existing cart data
      localStorage.removeItem('cart');
      localStorage.removeItem('cartItems');
      localStorage.setItem('cartCount', '0');

      console.log('Attempting login with:', { username: credentials.username, password: '***' });
      const response = await login(credentials);
      console.log('Login response:', response);

      if (response.token) {
        // Force a page reload to update the auth state
        window.location.href = '/';
      } else {
        setError('Invalid response from server');
        setIsLoading(false);
        return;
      }
      // Store auth data
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));

        navigate('/', { replace: true });

    } catch (error) {
      console.error('Login error in component:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);

      // Simplified error handling - extract message directly
      let errorMessage = 'Login failed. Please try again.';

      if (error.response?.data) {
        errorMessage = error.response.data.message || error.response.data.error || errorMessage;
      } else if (error.request) {
        errorMessage = 'Unable to connect to server. Please check your internet connection.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      console.log('Setting error message:', errorMessage);
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
    if (error) setError(''); // Clear error when user starts typing
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          mt: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 3
        }}
      >
        <Paper
          elevation={6}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 2,
            width: '100%',
            background: 'linear-gradient(to bottom right, #ffffff, #f8f9fa)'
          }}
        >
          <Avatar
            src={logo}
            sx={{
              width: 100,
              height: 100,
              mb: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}
          />

          <Typography component="h1" variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
            Welcome Back
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Sign in to continue shopping
          </Typography>

          {error && (
            <Alert
              severity="error"
              onClose={() => setError('')}
              sx={{
                mb: 2,
                width: '100%',
                animation: 'shake 0.5s'
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username or Email"
              name="username"
              autoComplete="username"
              value={credentials.username}
              onChange={handleChange}
              error={!!error}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color={error ? "error" : "primary"} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={credentials.password}
              onChange={handleChange}
              error={!!error}
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined color={error ? "error" : "primary"} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleTogglePassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography
                component={RouterLink}
                to="/forgot-password"
                variant="body2"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Forgot password?
              </Typography>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                mt: 2,
                mb: 2,
                py: 1.5,
                borderRadius: 1.5,
                fontWeight: 'bold',
                fontSize: '1rem',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 12px rgba(0,0,0,0.15)'
                }
              }}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>

            <Typography variant="body2" align="center" sx={{ mt: 2 }}>
              Don't have an account?{' '}
              <Typography
                component={RouterLink}
                to="/register"
                variant="body2"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Sign up
              </Typography>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}

export default Login;
